import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import {
  ArrowLeft,
  Palette,
  Upload,
  Check,
  Loader2,
  Image,
  Sun,
  Moon,
  RotateCcw,
  Info,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Color presets for quick selection
const COLOR_PRESETS = {
  corporate: { name: "Corporate Blue", primary: "#1e40af", secondary: "#64748b", accent: "#f59e0b" },
  emerald: { name: "Emerald", primary: "#059669", secondary: "#6b7280", accent: "#f59e0b" },
  slate: { name: "Slate", primary: "#475569", secondary: "#94a3b8", accent: "#0ea5e9" },
  indigo: { name: "Indigo", primary: "#4f46e5", secondary: "#6366f1", accent: "#f97316" },
  rose: { name: "Rose", primary: "#e11d48", secondary: "#f43f5e", accent: "#8b5cf6" },
  teal: { name: "Teal", primary: "#0d9488", secondary: "#14b8a6", accent: "#eab308" },
};

export default function BrandingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  // New versioned branding state
  const [branding, setBranding] = useState({
    logo_url: null,
    logo_dark_url: null,
    favicon_url: null,
    company_name: "",
    tagline: "",
    colors: {
      primary: "#1e40af",
      secondary: "#64748b",
      accent: "#f59e0b",
      background: "#ffffff",
      text: "#1e293b",
      error: "#dc2626",
      success: "#16a34a",
    },
    custom_css: "",
    header_text: "",
    footer_text: "",
    show_powered_by: true,
  });
  const [version, setVersion] = useState(1);
  const [etag, setEtag] = useState("");

  const fetchBranding = useCallback(async () => {
    try {
      // Use new versioned branding API
      const response = await axios.get(`${API}/branding`);
      const data = response.data;
      
      // Map API response to local state
      if (data.settings) {
        setBranding({
          logo_url: data.settings.logo_url || null,
          logo_dark_url: data.settings.logo_dark_url || null,
          favicon_url: data.settings.favicon_url || null,
          company_name: data.settings.company_name || "",
          tagline: data.settings.tagline || "",
          colors: data.settings.colors || {
            primary: "#1e40af",
            secondary: "#64748b",
            accent: "#f59e0b",
            background: "#ffffff",
            text: "#1e293b",
            error: "#dc2626",
            success: "#16a34a",
          },
          custom_css: data.settings.custom_css || "",
          header_text: data.settings.header_text || "",
          footer_text: data.settings.footer_text || "",
          show_powered_by: data.settings.show_powered_by ?? true,
        });
      }
      setVersion(data.version || 1);
      setEtag(data.etag || "");
    } catch (error) {
      console.error("Failed to load branding:", error);
      toast.error("Failed to load branding settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBranding();
  }, [fetchBranding]);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Use new versioned branding API
      const payload = {
        logo_url: branding.logo_url,
        logo_dark_url: branding.logo_dark_url,
        favicon_url: branding.favicon_url,
        company_name: branding.company_name || null,
        tagline: branding.tagline || null,
        colors: branding.colors,
        custom_css: branding.custom_css || null,
        header_text: branding.header_text || null,
        footer_text: branding.footer_text || null,
        show_powered_by: branding.show_powered_by,
      };
      
      const response = await axios.put(`${API}/branding`, payload);
      setVersion(response.data.version || version + 1);
      setEtag(response.data.etag || "");
      
      // Apply branding immediately to the page
      applyBrandingToPage(branding);
      
      toast.success("Branding updated and applied!");
    } catch (error) {
      console.error("Failed to save branding:", error);
      toast.error("Failed to update branding");
    } finally {
      setSaving(false);
    }
  };

  // Apply branding colors to the page immediately
  const applyBrandingToPage = (brandingData) => {
    const root = document.documentElement;
    const colors = brandingData.colors || {};
    
    // Helper to convert hex to HSL
    const hexToHSL = (hex) => {
      if (!hex) return null;
      hex = hex.replace('#', '');
      const r = parseInt(hex.substring(0, 2), 16) / 255;
      const g = parseInt(hex.substring(2, 4), 16) / 255;
      const b = parseInt(hex.substring(4, 6), 16) / 255;
      
      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      let h, s, l = (max + min) / 2;

      if (max === min) {
        h = s = 0;
      } else {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
          case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
          case g: h = ((b - r) / d + 2) / 6; break;
          case b: h = ((r - g) / d + 4) / 6; break;
          default: h = 0;
        }
      }
      return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
    };
    
    // Apply colors
    if (colors.primary) {
      const hsl = hexToHSL(colors.primary);
      if (hsl) root.style.setProperty('--primary', hsl);
      root.style.setProperty('--primary-700', colors.primary);
    }
    if (colors.secondary) {
      const hsl = hexToHSL(colors.secondary);
      if (hsl) root.style.setProperty('--secondary', hsl);
      root.style.setProperty('--secondary-600', colors.secondary);
    }
    if (colors.accent) {
      root.style.setProperty('--accent-color', colors.accent);
    }
    if (colors.background) {
      root.style.setProperty('--background-color', colors.background);
      document.body.style.backgroundColor = colors.background;
    }
    if (colors.text) {
      root.style.setProperty('--text-color', colors.text);
      document.body.style.color = colors.text;
    }
    
    // Apply favicon
    if (brandingData.favicon_url) {
      const link = document.querySelector("link[rel*='icon']") || document.createElement('link');
      link.type = 'image/x-icon';
      link.rel = 'shortcut icon';
      link.href = brandingData.favicon_url;
      document.getElementsByTagName('head')[0].appendChild(link);
    }
    
    // Apply custom CSS
    if (brandingData.custom_css) {
      let styleEl = document.getElementById('custom-branding-css');
      if (!styleEl) {
        styleEl = document.createElement('style');
        styleEl.id = 'custom-branding-css';
        document.head.appendChild(styleEl);
      }
      styleEl.textContent = brandingData.custom_css;
    }
    
    console.log('Branding applied to page:', colors);
  };

  const handleReset = async () => {
    if (!window.confirm("Reset all branding to defaults? This cannot be undone.")) return;
    
    setSaving(true);
    try {
      const response = await axios.post(`${API}/branding/reset`);
      if (response.data.settings) {
        setBranding({
          logo_url: response.data.settings.logo_url || null,
          logo_dark_url: response.data.settings.logo_dark_url || null,
          favicon_url: response.data.settings.favicon_url || null,
          company_name: response.data.settings.company_name || "",
          tagline: response.data.settings.tagline || "",
          colors: response.data.settings.colors || branding.colors,
          custom_css: response.data.settings.custom_css || "",
          header_text: response.data.settings.header_text || "",
          footer_text: response.data.settings.footer_text || "",
          show_powered_by: response.data.settings.show_powered_by ?? true,
        });
      }
      setVersion(response.data.version || 1);
      toast.success("Branding reset to defaults");
    } catch (error) {
      toast.error("Failed to reset branding");
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e, type = "logo") => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }

    // For now, convert to data URL (in production, would upload to storage)
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onloadend = () => {
        const dataUrl = reader.result;
        if (type === "logo") {
          setBranding({ ...branding, logo_url: dataUrl });
        } else if (type === "logo_dark") {
          setBranding({ ...branding, logo_dark_url: dataUrl });
        } else if (type === "favicon") {
          setBranding({ ...branding, favicon_url: dataUrl });
        }
        toast.success("Image uploaded - save to apply");
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error("Failed to upload image");
      setUploading(false);
    }
  };

  const handleApplyPreset = (presetKey) => {
    const preset = COLOR_PRESETS[presetKey];
    if (!preset) return;
    
    setBranding({
      ...branding,
      colors: {
        ...branding.colors,
        primary: preset.primary,
        secondary: preset.secondary,
        accent: preset.accent,
      }
    });
    toast.success(`Applied ${preset.name} theme - save to apply`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-primary-700 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <header className="bg-white border-b border-stone-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/")}
                className="text-stone-600"
                data-testid="branding-back-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="h-6 w-px bg-stone-200" />
              <div className="flex items-center gap-2">
                <Palette className="w-5 h-5 text-primary-700" />
                <h1 className="font-heading text-lg font-semibold text-stone-900">
                  Company Branding
                </h1>
                <span className="text-xs text-stone-400 ml-2">v{version}</span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={handleReset}
                disabled={saving}
                data-testid="branding-reset-btn"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Reset
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-primary-700 hover:bg-primary-700/90"
                data-testid="branding-save-btn"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Company Info */}
        <div className="bg-white border border-stone-200 rounded-lg p-6">
          <h3 className="font-heading text-lg font-medium text-stone-900 mb-4">
            Company Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Company Name</Label>
              <Input
                value={branding.company_name}
                onChange={(e) => setBranding({ ...branding, company_name: e.target.value })}
                placeholder="Your Company Name"
                className="mt-1"
                data-testid="branding-company-name"
              />
            </div>
            <div>
              <Label>Tagline</Label>
              <Input
                value={branding.tagline}
                onChange={(e) => setBranding({ ...branding, tagline: e.target.value })}
                placeholder="Your company tagline"
                className="mt-1"
                data-testid="branding-tagline"
              />
            </div>
          </div>
        </div>

        {/* Logo Upload */}
        <div className="bg-white border border-stone-200 rounded-lg p-6">
          <h3 className="font-heading text-lg font-medium text-stone-900 mb-4">
            Logos & Favicon
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Primary Logo */}
            <div>
              <Label className="mb-2 block">Primary Logo</Label>
              <div className="w-24 h-24 bg-stone-100 rounded-lg border-2 border-dashed border-stone-300 flex items-center justify-center overflow-hidden mb-2">
                {branding.logo_url ? (
                  <img
                    src={branding.logo_url}
                    alt="Company logo"
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <Image className="w-8 h-8 text-stone-400" />
                )}
              </div>
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleLogoUpload(e, "logo")}
                  disabled={uploading}
                />
                <Button variant="outline" size="sm" asChild disabled={uploading}>
                  <span>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload
                  </span>
                </Button>
              </label>
            </div>

            {/* Dark Mode Logo */}
            <div>
              <Label className="mb-2 block">Dark Mode Logo</Label>
              <div className="w-24 h-24 bg-stone-800 rounded-lg border-2 border-dashed border-stone-600 flex items-center justify-center overflow-hidden mb-2">
                {branding.logo_dark_url ? (
                  <img
                    src={branding.logo_dark_url}
                    alt="Dark mode logo"
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <Image className="w-8 h-8 text-stone-500" />
                )}
              </div>
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleLogoUpload(e, "logo_dark")}
                  disabled={uploading}
                />
                <Button variant="outline" size="sm" asChild disabled={uploading}>
                  <span>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload
                  </span>
                </Button>
              </label>
            </div>

            {/* Favicon */}
            <div>
              <Label className="mb-2 block">Favicon</Label>
              <div className="w-16 h-16 bg-stone-100 rounded-lg border-2 border-dashed border-stone-300 flex items-center justify-center overflow-hidden mb-2">
                {branding.favicon_url ? (
                  <img
                    src={branding.favicon_url}
                    alt="Favicon"
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <Image className="w-6 h-6 text-stone-400" />
                )}
              </div>
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleLogoUpload(e, "favicon")}
                  disabled={uploading}
                />
                <Button variant="outline" size="sm" asChild disabled={uploading}>
                  <span>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload
                  </span>
                </Button>
              </label>
            </div>
          </div>
          <p className="text-xs text-stone-500 mt-4">
            Recommended: Square images, at least 200x200px for logos, 32x32px for favicon
          </p>
        </div>

        {/* Color Theme */}
        <div className="bg-white border border-stone-200 rounded-lg p-6">
          <h3 className="font-heading text-lg font-medium text-stone-900 mb-4">
            Color Theme
          </h3>

          {/* Theme Presets */}
          <div className="mb-6">
            <Label className="text-sm text-stone-600 mb-3 block">Quick Presets</Label>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
              {Object.entries(COLOR_PRESETS).map(([key, preset]) => (
                <button
                  key={key}
                  onClick={() => handleApplyPreset(key)}
                  className="group relative"
                  title={preset.name}
                  data-testid={`preset-${key}`}
                >
                  <div
                    className="w-full aspect-square rounded-lg border-2 border-transparent hover:border-primary-500 transition-all overflow-hidden"
                    style={{ background: preset.primary }}
                  >
                    <div
                      className="w-full h-1/2"
                      style={{ background: preset.secondary }}
                    />
                  </div>
                  <span className="text-xs text-stone-600 mt-1 block text-center truncate">
                    {preset.name}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Custom Colors */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <Label>Primary Color</Label>
              <div className="flex items-center gap-2 mt-1">
                <input
                  type="color"
                  value={branding.colors.primary}
                  onChange={(e) => setBranding({ 
                    ...branding, 
                    colors: { ...branding.colors, primary: e.target.value }
                  })}
                  className="w-10 h-10 rounded border cursor-pointer"
                  data-testid="color-primary"
                />
                <Input
                  value={branding.colors.primary}
                  onChange={(e) => setBranding({ 
                    ...branding, 
                    colors: { ...branding.colors, primary: e.target.value }
                  })}
                  className="font-mono uppercase"
                />
              </div>
            </div>
            <div>
              <Label>Secondary Color</Label>
              <div className="flex items-center gap-2 mt-1">
                <input
                  type="color"
                  value={branding.colors.secondary}
                  onChange={(e) => setBranding({ 
                    ...branding, 
                    colors: { ...branding.colors, secondary: e.target.value }
                  })}
                  className="w-10 h-10 rounded border cursor-pointer"
                  data-testid="color-secondary"
                />
                <Input
                  value={branding.colors.secondary}
                  onChange={(e) => setBranding({ 
                    ...branding, 
                    colors: { ...branding.colors, secondary: e.target.value }
                  })}
                  className="font-mono uppercase"
                />
              </div>
            </div>
            <div>
              <Label>Accent Color</Label>
              <div className="flex items-center gap-2 mt-1">
                <input
                  type="color"
                  value={branding.colors.accent}
                  onChange={(e) => setBranding({ 
                    ...branding, 
                    colors: { ...branding.colors, accent: e.target.value }
                  })}
                  className="w-10 h-10 rounded border cursor-pointer"
                  data-testid="color-accent"
                />
                <Input
                  value={branding.colors.accent}
                  onChange={(e) => setBranding({ 
                    ...branding, 
                    colors: { ...branding.colors, accent: e.target.value }
                  })}
                  className="font-mono uppercase"
                />
              </div>
            </div>
          </div>

          {/* Additional Colors */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <Label className="text-xs">Background</Label>
              <div className="flex items-center gap-2 mt-1">
                <input
                  type="color"
                  value={branding.colors.background}
                  onChange={(e) => setBranding({ 
                    ...branding, 
                    colors: { ...branding.colors, background: e.target.value }
                  })}
                  className="w-8 h-8 rounded border cursor-pointer"
                />
                <Input
                  value={branding.colors.background}
                  onChange={(e) => setBranding({ 
                    ...branding, 
                    colors: { ...branding.colors, background: e.target.value }
                  })}
                  className="font-mono text-xs uppercase"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Text</Label>
              <div className="flex items-center gap-2 mt-1">
                <input
                  type="color"
                  value={branding.colors.text}
                  onChange={(e) => setBranding({ 
                    ...branding, 
                    colors: { ...branding.colors, text: e.target.value }
                  })}
                  className="w-8 h-8 rounded border cursor-pointer"
                />
                <Input
                  value={branding.colors.text}
                  onChange={(e) => setBranding({ 
                    ...branding, 
                    colors: { ...branding.colors, text: e.target.value }
                  })}
                  className="font-mono text-xs uppercase"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Success</Label>
              <div className="flex items-center gap-2 mt-1">
                <input
                  type="color"
                  value={branding.colors.success}
                  onChange={(e) => setBranding({ 
                    ...branding, 
                    colors: { ...branding.colors, success: e.target.value }
                  })}
                  className="w-8 h-8 rounded border cursor-pointer"
                />
                <Input
                  value={branding.colors.success}
                  onChange={(e) => setBranding({ 
                    ...branding, 
                    colors: { ...branding.colors, success: e.target.value }
                  })}
                  className="font-mono text-xs uppercase"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Error</Label>
              <div className="flex items-center gap-2 mt-1">
                <input
                  type="color"
                  value={branding.colors.error}
                  onChange={(e) => setBranding({ 
                    ...branding, 
                    colors: { ...branding.colors, error: e.target.value }
                  })}
                  className="w-8 h-8 rounded border cursor-pointer"
                />
                <Input
                  value={branding.colors.error}
                  onChange={(e) => setBranding({ 
                    ...branding, 
                    colors: { ...branding.colors, error: e.target.value }
                  })}
                  className="font-mono text-xs uppercase"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Additional Settings */}
        <div className="bg-white border border-stone-200 rounded-lg p-6">
          <h3 className="font-heading text-lg font-medium text-stone-900 mb-4">
            Additional Settings
          </h3>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label>Show &ldquo;Powered by&rdquo; Badge</Label>
                <p className="text-xs text-stone-500">Display attribution badge in footer</p>
              </div>
              <Switch
                checked={branding.show_powered_by}
                onCheckedChange={(checked) => setBranding({ ...branding, show_powered_by: checked })}
                data-testid="show-powered-by-toggle"
              />
            </div>
            
            <div>
              <Label>Header Text</Label>
              <Input
                value={branding.header_text}
                onChange={(e) => setBranding({ ...branding, header_text: e.target.value })}
                placeholder="Optional header announcement"
                className="mt-1"
                data-testid="branding-header-text"
              />
            </div>
            
            <div>
              <Label>Footer Text</Label>
              <Input
                value={branding.footer_text}
                onChange={(e) => setBranding({ ...branding, footer_text: e.target.value })}
                placeholder="Optional footer text"
                className="mt-1"
                data-testid="branding-footer-text"
              />
            </div>

            <div>
              <Label>Custom CSS (Advanced)</Label>
              <Textarea
                value={branding.custom_css}
                onChange={(e) => setBranding({ ...branding, custom_css: e.target.value })}
                placeholder="/* Add custom CSS styles */"
                className="mt-1 font-mono text-xs min-h-[100px]"
                data-testid="branding-custom-css"
              />
              <p className="text-xs text-stone-400 mt-1">
                Advanced: Add custom CSS to override default styles
              </p>
            </div>
          </div>
        </div>

        {/* Preview */}
        <div className="bg-white border border-stone-200 rounded-lg p-6">
          <h3 className="font-heading text-lg font-medium text-stone-900 mb-4">
            Preview
          </h3>
          <div
            className="rounded-lg p-6"
            style={{ background: branding.colors.background }}
          >
            <div className="flex items-center gap-3 mb-4">
              {branding.logo_url ? (
                <img
                  src={branding.logo_url}
                  alt=""
                  className="w-10 h-10 rounded-lg object-contain"
                />
              ) : (
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ background: branding.colors.primary }}
                >
                  <span className="text-white font-bold">
                    {branding.company_name?.charAt(0) || "M"}
                  </span>
                </div>
              )}
              <div>
                <span
                  className="font-semibold block"
                  style={{ color: branding.colors.primary }}
                >
                  {branding.company_name || "Your Company"}
                </span>
                {branding.tagline && (
                  <span className="text-xs" style={{ color: branding.colors.secondary }}>
                    {branding.tagline}
                  </span>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                className="px-4 py-2 rounded-lg text-white text-sm"
                style={{ background: branding.colors.primary }}
              >
                Primary Button
              </button>
              <button
                className="px-4 py-2 rounded-lg text-white text-sm"
                style={{ background: branding.colors.secondary }}
              >
                Secondary
              </button>
              <button
                className="px-4 py-2 rounded-lg text-white text-sm"
                style={{ background: branding.colors.accent }}
              >
                Accent
              </button>
              <button
                className="px-4 py-2 rounded-lg text-white text-sm"
                style={{ background: branding.colors.success }}
              >
                Success
              </button>
              <button
                className="px-4 py-2 rounded-lg text-white text-sm"
                style={{ background: branding.colors.error }}
              >
                Error
              </button>
            </div>
          </div>
        </div>

        {/* Version Info */}
        <div className="bg-stone-50 border border-stone-200 rounded-lg p-4 flex items-center gap-3 mb-24">
          <Info className="w-5 h-5 text-stone-400" />
          <div className="text-sm text-stone-600">
            <span className="font-medium">Version {version}</span>
            {etag && <span className="text-stone-400 ml-2">• ETag: {etag}</span>}
            <p className="text-xs text-stone-400 mt-0.5">
              Branding changes are automatically versioned for cache invalidation
            </p>
          </div>
        </div>
      </main>

      {/* Sticky Save Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-stone-200 shadow-lg z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-stone-600">
              Make changes above, then click <strong>Save</strong> to apply.
            </p>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={handleReset}
                disabled={saving}
                data-testid="branding-reset-bottom-btn"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Reset to Defaults
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="branding-save-bottom-btn"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                Save All Changes
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
